import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Users as UsersIcon,
  MapPin,
  Building2,
  Tags,
  LogOut,
  Home,
  Shield,
  FileText,
  Palette,
  Calculator,
  Menu,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';

const navItems = [
  { to: '/admin', end: true, icon: Home, label: 'Overview' },
  { to: '/admin/quotes', icon: FileText, label: 'Quotes' },
  { to: '/admin/price-lists', icon: Tags, label: 'Price lists' },
  { to: '/admin/customers', icon: Building2, label: 'Customers' },
  { to: '/admin/regions', icon: MapPin, label: 'Regions', adminOnly: true },
  { to: '/admin/users', icon: UsersIcon, label: 'Users', adminOnly: true },
  { to: '/admin/settings', icon: Palette, label: 'Settings', adminOnly: true },
];

const AdminLayout = () => {
  const { user, logout, isAdmin } = useAuth();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the drawer whenever the user navigates (via NavLink click).
  const activeLabel = navItems.find((n) =>
    n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)
  )?.label;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeDrawer = () => setMobileOpen(false);

  const Sidebar = (
    <aside
      className={
        // On desktop: static w-64 column. On mobile: fixed off-canvas drawer.
        'bg-[#0a0a0d] md:bg-black/40 border-r border-white/5 backdrop-blur-md flex flex-col ' +
        // Mobile positioning
        'fixed inset-y-0 left-0 z-50 w-[85vw] max-w-[320px] transform transition-transform duration-200 ease-out ' +
        (mobileOpen ? 'translate-x-0' : '-translate-x-full') + ' ' +
        // Desktop reset: static sticky column
        'md:static md:translate-x-0 md:w-64 md:h-screen md:sticky md:top-0 md:z-auto md:max-w-none'
      }
    >
      <div className="h-16 flex items-center justify-between gap-2.5 px-5 border-b border-white/5">
        <Link to="/" className="flex items-center gap-2.5 min-w-0" onClick={closeDrawer}>
          <span
            className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, rgba(34,211,238,0.25), rgba(168,85,247,0.25))' }}
          >
            <Calculator className="w-4 h-4 text-white/90" />
          </span>
          <div className="min-w-0">
            <div className="text-[13.5px] font-semibold text-white leading-none">Price Calculator</div>
            <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-white/40 truncate">
              Brand · {brand.name}
            </div>
          </div>
        </Link>
        <button
          onClick={closeDrawer}
          data-testid="admin-drawer-close"
          className="md:hidden h-9 w-9 flex items-center justify-center rounded-lg text-white/60 hover:text-white hover:bg-white/[0.06]"
          aria-label="Close menu"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-5 pt-5 pb-3">
        <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/40">Admin</div>
      </div>

      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          if (item.adminOnly && !isAdmin) return null;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={closeDrawer}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13.5px] transition-colors ${
                  isActive
                    ? 'bg-white/[0.06] text-white'
                    : 'text-white/60 hover:text-white hover:bg-white/[0.03]'
                }`
              }
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="p-3 border-t border-white/5">
        <div className="px-3 py-2 rounded-lg bg-white/[0.02]">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-cyan-400 to-purple-500 flex items-center justify-center text-black font-bold text-sm">
              {(user?.full_name || user?.email || 'U').slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] text-white truncate">{user?.full_name}</div>
              <div className="text-[11px] text-white/45 truncate">{user?.email}</div>
            </div>
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            <Shield className="w-3 h-3 text-emerald-400 flex-shrink-0" />
            <span className="text-[10px] tracking-[0.16em] uppercase font-semibold text-emerald-300">
              {user?.role?.replace('_', ' ')}
            </span>
            {user?.partner_name && (
              <span className="text-[10px] text-white/40 truncate">· {user.partner_name}</span>
            )}
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="mt-2 w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-[13px] text-white/60 hover:text-white hover:bg-white/[0.03] transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0d] text-white md:flex">
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-30 h-14 bg-black/80 backdrop-blur-md border-b border-white/10 flex items-center justify-between px-4">
        <button
          onClick={() => setMobileOpen(true)}
          data-testid="admin-drawer-toggle"
          className="h-10 w-10 flex items-center justify-center rounded-lg text-white/80 hover:bg-white/[0.06]"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <Calculator className="w-4 h-4 text-cyan-300 flex-shrink-0" />
          <div className="text-[13px] font-semibold text-white truncate">
            {activeLabel || 'Price Calculator'}
          </div>
        </div>
        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-cyan-400 to-purple-500 flex items-center justify-center text-black font-bold text-sm">
          {(user?.full_name || user?.email || 'U').slice(0, 1).toUpperCase()}
        </div>
      </div>

      {/* Backdrop for mobile drawer */}
      {mobileOpen && (
        <button
          onClick={closeDrawer}
          aria-label="Dismiss menu"
          className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        />
      )}

      {Sidebar}

      {/* Main content — min-w-0 lets wide tables scroll within */}
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
